import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Mail, ShoppingBag, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { setAdvancedMatching } from "@/lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHOWN_KEY = "tracksy-exit-shown";
const EMAIL_KEY = "tracksy-email";

/**
 * Site-wide exit-intent email capture + bottom "Resume checkout" pill.
 *
 * - Exit modal fires once per session on first mouse-leave or tab-blur, offering
 *   10% off (SAVE10). Captures email + cart server-side so the abandoned-cart
 *   cron can send a recovery email even if the shopper never returns.
 * - Resume pill shows at bottom-right on any page when the cart has items and
 *   the drawer is closed — one-tap jump back to checkout.
 *
 * Both components are no-op on first visit when cart is empty.
 */
export default function ExitIntentCapture() {
  const { items, subtotal, currency, trackerCount, setIsOpen, isOpen } = useCart();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(EMAIL_KEY) || ""; } catch { return ""; }
  });
  const [submitting, setSubmitting] = useState(false);

  // Fire the modal when the shopper's intent-to-leave is detected:
  //   • desktop: cursor exits the viewport through the top (reaching for tab bar)
  //   • mobile:  document loses visibility (tab backgrounded or app switched)
  // Only once per session, only when the cart isn't empty, only when the drawer
  // itself isn't already open (that would double-stack with the inline SAVE10 overlay).
  useEffect(() => {
    const hasShown = () => { try { return sessionStorage.getItem(SHOWN_KEY) === "1"; } catch { return false; } };
    const markShown = () => { try { sessionStorage.setItem(SHOWN_KEY, "1"); } catch {} };

    const trigger = () => {
      if (hasShown() || items.length === 0 || isOpen) return;
      markShown();
      setOpen(true);
    };
    const onMouseLeave = (e) => { if (e.clientY <= 0) trigger(); };
    const onVisibility = () => { if (document.visibilityState === "hidden") trigger(); };
    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [items.length, isOpen]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      try { localStorage.setItem(EMAIL_KEY, clean); } catch {}
      setAdvancedMatching({ email: clean });
      await fetch(`${BACKEND_URL}/api/cart/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: clean,
          items: items.map((i) => ({ packId: i.packId, color: i.color, qty: i.qty })),
          subtotal, currency,
          source: "exit-intent",
        }),
      }).catch(() => {});
      toast.success("10% off applied — SAVE10", {
        description: "Code auto-applies at checkout. We also saved your cart.",
      });
      setOpen(false);
      // Pop the drawer open so the shopper sees SAVE10 landed on the total.
      setIsOpen(true);
      try { window.dispatchEvent(new CustomEvent("tracksy:apply-promo", { detail: "SAVE10" })); } catch {}
    } finally {
      setSubmitting(false);
    }
  };

  const canShowPill = items.length > 0 && !isOpen && !open;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="exit-modal"
            data-testid="exit-intent-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="exit-intent-close"
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 rounded-full p-2 text-tracksy-sub transition-colors hover:bg-tracksy-cream"
                aria-label="Close"
              ><X className="h-4 w-4" /></button>
              <div className="bg-tracksy-orange px-6 pt-6 pb-5 text-white">
                <p className="text-[11px] font-extrabold uppercase tracking-widest opacity-90">Wait — one thing</p>
                <h3 className="mt-1 font-heading text-2xl font-extrabold leading-tight">
                  Keep your {trackerCount || items.reduce((s, i) => s + i.packId * i.qty, 0)}-pack and we'll knock <span className="underline underline-offset-4">10% off</span>
                </h3>
                <p className="mt-2 text-sm opacity-95">Email it to yourself in one tap. Code auto-applies at checkout.</p>
              </div>
              <form onSubmit={submit} className="px-6 py-5">
                <label className="flex items-center gap-2 rounded-full border border-tracksy-ink/15 bg-white px-4 py-3 focus-within:border-tracksy-orange">
                  <Mail className="h-4 w-4 text-tracksy-sub" />
                  <input
                    data-testid="exit-intent-email-input"
                    autoFocus
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-tracksy-sub"
                  />
                </label>
                <button
                  data-testid="exit-intent-submit"
                  type="submit"
                  disabled={submitting}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-tracksy-ink py-3 text-sm font-extrabold uppercase tracking-wider text-white transition-colors hover:bg-tracksy-ink/90 disabled:opacity-70"
                >
                  Send my 10% off code
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[10px] font-semibold text-tracksy-sub">
                  <ShieldCheck className="h-3 w-3 text-tracksy-green" />
                  No spam. Unsubscribe any time.
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {canShowPill && (
          <motion.button
            key="resume-pill"
            data-testid="resume-checkout-pill"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-4 right-4 z-[55] flex items-center gap-2 rounded-full bg-tracksy-orange px-4 py-3 text-sm font-extrabold text-white shadow-xl shadow-tracksy-orange/30 transition-transform hover:scale-105"
          >
            <ShoppingBag className="h-4 w-4" />
            <span>Resume · {currency === "USD" ? "US$" : "$"}{Math.round(subtotal)}</span>
            <ArrowRight className="h-4 w-4" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
